/**
 * ProjectSidebar — narrow right rail with two tabs:
 *   Details — name, dates, client, status, priority, budget summary.
 *   People  — managers + members with a hide toggle and a Manage button.
 */
import React, { useState } from "react";
import { toast } from "../../../utils/toast";
import { Settings2, EyeOff } from "lucide-react";
import { wsApi } from "../wsApi";
import { T, Tabs } from "../../../components/em/ui";
import { PROJECT_STATUS, PRIORITY, fmtDate } from "../wsConstants";
import { money, Avatar } from "../wsUi";
import ManageMembersModal from "./ManageMembersModal";

export default function ProjectSidebar({ project, users, canManage, onChanged }) {
  const [tab, setTab] = useState("details");
  const [manage, setManage] = useState(false);
  const toggleHide = async (m) => {
    try { await wsApi.updateProjectMember(project.id, m.user_id, { hidden: m.hidden ? 0 : 1 }); onChanged(); }
    catch { toast.error("Could not update"); }
  };

  const members = project.members || [];

  return (
    <aside style={{ width: 300, flexShrink: 0 }}>
      <div style={{ background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden", position: "sticky", top: 16 }}>
        {/* Tabs — shared segmented control (same as Attendance/Leaves) */}
        <div style={{ padding: "12px 12px 0" }}>
          <Tabs
            tabs={[
              { key: "details", label: "Details" },
              { key: "people", label: `People (${members.length})` },
            ]}
            active={tab} onChange={setTab}
            style={{ display: "flex", width: "100%", boxSizing: "border-box" }}
          />
        </div>

        <div style={{ padding: 16 }}>
          {tab === "details" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Row label="Name" value={project.name} />
              <Row label="Client" value={project.client_name || "—"} />
              <Row label="Status" value={(PROJECT_STATUS[project.status] || PROJECT_STATUS.planning).label} />
              <Row label="Priority" value={(PRIORITY[project.priority] || PRIORITY.medium).label} />
              <Row label="Start date" value={project.start_date ? fmtDate(project.start_date) : "—"} />
              <Row label="End date" value={project.deadline ? fmtDate(project.deadline) : "—"} />
              <Row label="Budget type" value={project.budget_type ? project.budget_type[0].toUpperCase() + project.budget_type.slice(1) : "—"} />
              <Row label="Budget" value={project.budget != null ? money(project.budget) : "—"} />
              <Row label="Hourly quote" value={project.hourly_quote != null ? money(project.hourly_quote) : "—"} />
            </div>
          ) : (
            <div>
              {canManage && (
                <button onClick={() => setManage(true)}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 7, width: "100%",
                    padding: "8px 0", marginBottom: 12, borderRadius: 8, border: `1px solid ${T.border}`,
                    background: "var(--em-bg-card)", color: T.fg2, cursor: "pointer", font: `600 12px ${T.font}` }}>
                  <Settings2 size={14} /> Manage people
                </button>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {members.length === 0 && <div style={{ color: T.fg7, font: `400 12px ${T.font}`, textAlign: "center", padding: 10 }}>No one here.</div>}
                {members.map((m) => (
                  <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 8, opacity: m.hidden ? 0.5 : 1 }}>
                    <Avatar member={m} size={26} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ font: `600 12.5px ${T.font}`, color: T.fg1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {m.full_name || m.username}
                        {m.role === "manager" && <span style={{ marginLeft: 6, font: `700 9px ${T.font}`, color: T.orange, textTransform: "uppercase" }}>PM</span>}
                        {!!m.hidden && <EyeOff size={11} style={{ marginLeft: 5, verticalAlign: "middle", color: T.fg7 }} />}
                      </div>
                    </div>
                    {canManage && (
                      <button title={m.hidden ? "Show" : "Hide"} onClick={() => toggleHide(m)}
                        style={{ background: "none", border: 0, cursor: "pointer", color: T.fg7, display: "flex",
                          alignItems: "center", justifyContent: "center", flexShrink: 0,
                          width: 24, height: 24, padding: 0, margin: 0, lineHeight: 1 }}>
                        <EyeOff size={14} style={{ width: 14, height: 14, top: 0 }} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {manage && (
        <ManageMembersModal project={project} users={users}
          onClose={() => setManage(false)} onChanged={onChanged} />
      )}
    </aside>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div style={{ font: `600 10.5px ${T.font}`, color: T.fg7, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <div style={{ font: `500 13px ${T.font}`, color: T.fg1, marginTop: 2, wordBreak: "break-word" }}>{value}</div>
    </div>
  );
}
